"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Menu, X } from "lucide-react"

export default function Header() {
	const [isMenuOpen, setIsMenuOpen] = useState(false)
	const [isScrolled, setIsScrolled] = useState(false)

	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 100)
		}
		window.addEventListener("scroll", handleScroll)
		return () => window.removeEventListener("scroll", handleScroll)
	}, [])

	const scrollToSection = (sectionId: string) => {
		const element = document.getElementById(sectionId)
		if (element) {
			element.scrollIntoView({ behavior: "smooth" })
			setIsMenuOpen(false)
		}
	}

	return (
		<header className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled ? "bg-white/95 backdrop-blur-md shadow-lg" : "bg-white/90 backdrop-blur-md"}`}>
			<nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="flex justify-between items-center py-3 sm:py-4">
					{/* Brand */}
					<div className="flex-shrink-0 min-w-0">
						<h2 className="text-lg sm:text-2xl font-semibold text-slate-900 truncate">Оксана Заякина</h2>
						<p className="text-xs sm:text-sm text-slate-600 -mt-1 hidden sm:block">Клинический психолог • Гипнотерапевт</p>
					</div>

					{/* Desktop Navigation */}
					<div className="hidden md:flex items-center space-x-4 lg:space-x-8">
						<button onClick={() => scrollToSection("about")} className="text-slate-700 hover:text-indigo-600 font-medium transition-colors text-sm lg:text-base">
							Обо мне
						</button>
						<button onClick={() => scrollToSection("services")} className="text-slate-700 hover:text-indigo-600 font-medium transition-colors text-sm lg:text-base">
							Услуги
						</button>
						<button onClick={() => scrollToSection("prices")} className="text-slate-700 hover:text-indigo-600 font-medium transition-colors text-sm lg:text-base">
							Цены
						</button>
						<button onClick={() => scrollToSection("contact")} className="text-slate-700 hover:text-indigo-600 font-medium transition-colors text-sm lg:text-base">
							Контакты
						</button>
						<Button onClick={() => scrollToSection("booking")} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 lg:px-6 py-2 rounded-lg transition-all duration-300 hover:scale-105 text-sm lg:text-base">
							Записаться
						</Button>
						{/* Contacts block */}
						<div className="flex items-center space-x-3 lg:space-x-3 ml-4 pl-4 border-l border-slate-200">
							<a href="tel:+79226268052" className="text-slate-900 hover:text-indigo-600 font-semibold text-sm lg:text-base whitespace-nowrap">
								+7 (922) 626-80-52
							</a>
							<a href="https://vk.com/oxanazayakina" target="_blank" rel="noopener noreferrer" aria-label="VK" className="text-slate-500 hover:text-indigo-600">
								<svg width="20" height="20" viewBox="0 0 101 100" fill="none" xmlns="http://www.w3.org/2000/svg">
									<g clip-path="url(#clip0_2_16)">
										<path
											fill-rule="evenodd"
											clip-rule="evenodd"
											d="M7.52944 7.02944C0.5 14.0589 0.5 25.3726 0.5 48V52C0.5 74.6274 0.5 85.9411 7.52944 92.9706C14.5589 100 25.8726 100 48.5 100H52.5C75.1274 100 86.4411 100 93.4706 92.9706C100.5 85.9411 100.5 74.6274 100.5 52V48C100.5 25.3726 100.5 14.0589 93.4706 7.02944C86.4411 0 75.1274 0 52.5 0H48.5C25.8726 0 14.5589 0 7.52944 7.02944ZM17.3752 30.4169C17.9168 56.4169 30.9167 72.0418 53.7084 72.0418H55.0003V57.1668C63.3753 58.0001 69.7082 64.1252 72.2498 72.0418H84.0835C80.8335 60.2085 72.2914 53.6668 66.9581 51.1668C72.2914 48.0835 79.7915 40.5835 81.5831 30.4169H70.8328C68.4995 38.6669 61.5836 46.1668 55.0003 46.8751V30.4169H44.2499V59.2501C37.5833 57.5835 29.1668 49.5002 28.7918 30.4169H17.3752Z"
											fill="currentColor"
										/>
									</g>
									<defs>
										<clipPath id="clip0_2_16">
											<rect width="100" height="100" fill="white" transform="translate(0.5)" />
										</clipPath>
									</defs>
								</svg>
							</a>
							<a href="https://t.me/OxanaZayakina" target="_blank" rel="noopener noreferrer" aria-label="Telegram" className="text-slate-500 hover:text-indigo-600">
								<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
									<path d="M12 2C6.477 2 2 6.477 2 12c0 5.523 4.477 10 10 10s10-4.477 10-10c0-5.523-4.477-10-10-10zm3.93 7.62l-1.44 6.82c-.11.5-.4.62-.81.39l-2.25-1.66-1.09 1.05c-.12.12-.22.22-.45.22l.16-2.28 4.16-3.76c.18-.16-.04-.25-.28-.09l-5.15 3.24-2.22-.69c-.48-.15-.49-.48.1-.71l8.68-3.35c.4-.15.75.09.62.7z" />
								</svg>
							</a>
						</div>
					</div>

					{/* Mobile menu button */}
					<div className="md:hidden">
						<Button variant="ghost" size="icon" onClick={() => setIsMenuOpen(!isMenuOpen)} className="h-8 w-8 sm:h-10 sm:w-10">
							{isMenuOpen ? <X className="h-5 w-5 sm:h-6 sm:w-6" /> : <Menu className="h-5 w-5 sm:h-6 sm:w-6" />}
						</Button>
					</div>
				</div>

				{/* Mobile Navigation */}
				{isMenuOpen && (
					<div className="md:hidden bg-white border-t border-slate-200 py-4">
						<div className="flex flex-col space-y-3 sm:space-y-4">
							<button onClick={() => scrollToSection("about")} className="text-slate-700 hover:text-indigo-600 font-medium text-left text-sm sm:text-base py-1">
								Обо мне
							</button>
							<button onClick={() => scrollToSection("services")} className="text-slate-700 hover:text-indigo-600 font-medium text-left text-sm sm:text-base py-1">
								Услуги
							</button>
							<button onClick={() => scrollToSection("prices")} className="text-slate-700 hover:text-indigo-600 font-medium text-left text-sm sm:text-base py-1">
								Цены
							</button>
							<button onClick={() => scrollToSection("contact")} className="text-slate-700 hover:text-indigo-600 font-medium text-left text-sm sm:text-base py-1">
								Контакты
							</button>
							<Button onClick={() => scrollToSection("booking")} className="bg-indigo-600 hover:bg-indigo-700 text-white w-full text-sm sm:text-base py-2.5">
								Записаться
							</Button>
						</div>
						{/* Contacts block for mobile */}
						<div className="flex items-center space-x-4 mt-6 justify-center">
							<a href="tel:+79226268052" className="text-slate-900 hover:text-indigo-600 font-semibold text-base whitespace-nowrap">
								+7 (922) 626-80-52
							</a>
							<a href="https://vk.com/oxanazayakina" target="_blank" rel="noopener noreferrer" aria-label="VK" className="text-slate-500 hover:text-indigo-600">
								<svg width="20" height="20" viewBox="0 0 101 100" fill="none" xmlns="http://www.w3.org/2000/svg">
									<g clip-path="url(#clip0_2_16)">
										<path
											fill-rule="evenodd"
											clip-rule="evenodd"
											d="M7.52944 7.02944C0.5 14.0589 0.5 25.3726 0.5 48V52C0.5 74.6274 0.5 85.9411 7.52944 92.9706C14.5589 100 25.8726 100 48.5 100H52.5C75.1274 100 86.4411 100 93.4706 92.9706C100.5 85.9411 100.5 74.6274 100.5 52V48C100.5 25.3726 100.5 14.0589 93.4706 7.02944C86.4411 0 75.1274 0 52.5 0H48.5C25.8726 0 14.5589 0 7.52944 7.02944ZM17.3752 30.4169C17.9168 56.4169 30.9167 72.0418 53.7084 72.0418H55.0003V57.1668C63.3753 58.0001 69.7082 64.1252 72.2498 72.0418H84.0835C80.8335 60.2085 72.2914 53.6668 66.9581 51.1668C72.2914 48.0835 79.7915 40.5835 81.5831 30.4169H70.8328C68.4995 38.6669 61.5836 46.1668 55.0003 46.8751V30.4169H44.2499V59.2501C37.5833 57.5835 29.1668 49.5002 28.7918 30.4169H17.3752Z"
											fill="currentColor"
										/>
									</g>
									<defs>
										<clipPath id="clip0_2_16">
											<rect width="100" height="100" fill="white" transform="translate(0.5)" />
										</clipPath>
									</defs>
								</svg>
							</a>
							<a href="https://t.me/OxanaZayakina" target="_blank" rel="noopener noreferrer" aria-label="Telegram" className="text-slate-500 hover:text-indigo-600">
								<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
									<path d="M12 2C6.477 2 2 6.477 2 12c0 5.523 4.477 10 10 10s10-4.477 10-10c0-5.523-4.477-10-10-10zm3.93 7.62l-1.44 6.82c-.11.5-.4.62-.81.39l-2.25-1.66-1.09 1.05c-.12.12-.22.22-.45.22l.16-2.28 4.16-3.76c.18-.16-.04-.25-.28-.09l-5.15 3.24-2.22-.69c-.48-.15-.49-.48.1-.71l8.68-3.35c.4-.15.75.09.62.7z" />
								</svg>
							</a>
						</div>
					</div>
				)}
			</nav>
		</header>
	)
}
